import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Navbar,
  Button,
  LoadingSpinner,
  ErrorState,
  DevCardElement,
} from '../components';
import { walletService } from '../services';
import { useUser, useAuth } from '../context';
import {
  isStripeDevMode,
  mockConfirmCardSetup,
} from '../utils/stripeDevMode';

// Card element styling to match app theme
const cardElementOptions = {
  style: {
    base: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize: '14px',
      color: '#000000',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
};

/**
 * Wallet Page - P$ Management
 * View balance, top up, and see transaction history
 */
export default function WalletPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile, walletBalance, refreshWallet } = useUser();
  const { logout } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const devCardRef = useRef();
  const devMode = isStripeDevMode();

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [topUpSuccess, setTopUpSuccess] = useState(false);
  const [cardError, setCardError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [cardComplete, setCardComplete] = useState(false);

  // Fetch wallet data
  const fetchWallet = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [walletResult, transactionsResult] = await Promise.all([
        walletService.getWallet(),
        walletService.getTransactions(0, 50),
      ]);

      if (walletResult.success) {
        setWallet(walletResult.data);
      } else {
        setError(walletResult.error || 'Failed to load wallet');
      }

      if (transactionsResult.success) {
        setTransactions(transactionsResult.data || []);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  // Handle Stripe Checkout redirect params (legacy support)
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setTopUpSuccess(true);
      // Clear URL params
      window.history.replaceState({}, '', '/wallet');
      // Refresh wallet to show new balance
      fetchWallet();
      refreshWallet();
      setTimeout(() => setTopUpSuccess(false), 5000);
    } else if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled');
      window.history.replaceState({}, '', '/wallet');
    }
  }, [searchParams, fetchWallet, refreshWallet]);

  // Handle card element change
  const handleCardChange = (e) => {
    setCardError(e.error ? e.error.message : null);
    setCardComplete(e.complete);
  };

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount < 1 || amount > 1000) {
      return;
    }

    // In normal mode, require stripe and elements
    if (!devMode && (!stripe || !elements)) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Convert dollars to cents
      const amountInCents = Math.round(amount * 100);

      // Create PaymentIntent
      const result = await walletService.createPaymentIntent(amountInCents);
      if (!result.success) {
        setError(result.error || 'Failed to create payment');
        setIsProcessing(false);
        return;
      }

      const { clientSecret } = result.data;

      if (devMode) {
        // Dev mode: Use fake payment method from DevCardElement
        const cardData = devCardRef.current?.getCardData();
        if (!cardData || !cardData.number) {
          setError('Please enter card details');
          setIsProcessing(false);
          return;
        }

        // Simulate card payment (may throw for test decline cards)
        const mockResult = mockConfirmCardSetup(cardData);
        if (mockResult.error) {
          setError(mockResult.error.message);
          setIsProcessing(false);
          return;
        }

        // Dev mode success - add funds directly
        const topUpResult = await walletService.topUp(amountInCents);
        if (topUpResult.success) {
          setTopUpSuccess(true);
          setShowTopUp(false);
          setTopUpAmount('');
          devCardRef.current?.clear();
          await fetchWallet();
          refreshWallet();
          setTimeout(() => setTopUpSuccess(false), 5000);
        } else {
          setError(topUpResult.error || 'Failed to add funds');
        }
      } else {
        // Production mode: Use real Stripe
        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          },
        });

        if (stripeError) {
          setError(stripeError.message);
        } else if (paymentIntent.status === 'succeeded') {
          setTopUpSuccess(true);
          setShowTopUp(false);
          setTopUpAmount('');
          // Short delay for webhook to process
          setTimeout(async () => {
            await fetchWallet();
            refreshWallet();
          }, 1000);
          setTimeout(() => setTopUpSuccess(false), 5000);
        }
      }
    } catch (err) {
      setError('Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type) => {
    const icons = {
      TopUp: '↑',
      Commitment: '→',
      CommitmentRefund: '←',
      FlakeFee: '✕',
      HostFlakeFeeEarning: '★',
      HostEventEarning: '⊕',
      HostEscrowRelease: '✓',
      Bonus: '♦',
      Upgrade: '▲',
      AdminAdjustment: '◆',
    };
    return icons[type] || '•';
  };

  const getTransactionColor = (amount) => {
    return amount >= 0 ? 'text-green-600' : 'text-red-600';
  };

  // Top-up amount presets
  const presetAmounts = [5, 10, 25, 50, 100];

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/')}
      />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-2">
              P$ WALLET
            </h1>
            <p className="font-mono text-sm text-gray-600">
              Your party coins
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="font-mono text-sm text-gray-500 hover:text-black uppercase tracking-wider"
          >
            ← BACK
          </button>
        </div>

        {/* Success Message */}
        {topUpSuccess && (
          <div className="mb-6 p-4 border-2 border-green-500 bg-green-50">
            <p className="font-mono text-sm text-green-800 text-center uppercase tracking-wider">
              P$ added successfully!
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="p-12">
            <LoadingSpinner label="Loading wallet..." />
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={fetchWallet} />
        ) : (
          <>
            {/* Balance Card */}
            <div className="border-2 border-black bg-white p-8 mb-8">
              <div className="text-center mb-6">
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                  CURRENT BALANCE
                </p>
                <p className="font-mono text-5xl md:text-6xl font-bold text-black">
                  P${wallet ? (wallet.balance / 100).toFixed(2) : '0.00'}
                </p>
                {wallet && wallet.escrowBalance > 0 && (
                  <p className="font-mono text-sm text-gray-500 mt-2">
                    + P${(wallet.escrowBalance / 100).toFixed(2)} held in escrow
                  </p>
                )}
              </div>

              <Button
                variant="primary"
                fullWidth
                size="lg"
                onClick={() => setShowTopUp(!showTopUp)}
              >
                {showTopUp ? 'CANCEL' : '+ ADD P$'}
              </Button>

              {/* Top-up Form */}
              {showTopUp && (
                <div className="mt-6 pt-6 border-t-2 border-black">
                  <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-4 text-center">
                    SELECT AMOUNT
                  </p>

                  {/* Preset Amounts */}
                  <div className="grid grid-cols-5 gap-2 mb-4">
                    {presetAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setTopUpAmount(amount.toString())}
                        className={`
                          py-3 border-2 font-mono text-sm font-bold transition-colors
                          ${topUpAmount === amount.toString()
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-black border-black hover:bg-gray-100'
                          }
                        `}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <div className="mb-4">
                    <div className="flex items-center border-2 border-black">
                      <span className="px-4 py-3 bg-gray-100 border-r-2 border-black font-mono text-sm font-bold">
                        $
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="1000"
                        step="0.01"
                        value={topUpAmount}
                        onChange={(e) => setTopUpAmount(e.target.value)}
                        placeholder="Custom amount"
                        className="flex-1 px-4 py-3 font-mono text-sm focus:outline-none"
                      />
                    </div>
                    <p className="mt-1 font-mono text-xs text-gray-500">
                      Min $1.00 • Max $1,000.00
                    </p>
                  </div>

                  {/* Card Element */}
                  <div className="mb-4">
                    <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                      CARD DETAILS
                    </p>
                    <div
                      className={`
                        px-4 py-4
                        border-2 border-black
                        bg-white
                        ${cardError ? 'border-red-600' : ''}
                      `}
                    >
                      {devMode ? (
                        <DevCardElement
                          ref={devCardRef}
                          onChange={handleCardChange}
                          disabled={isProcessing}
                        />
                      ) : (
                        <CardElement options={cardElementOptions} onChange={handleCardChange} />
                      )}
                    </div>
                    {cardError && (
                      <p className="mt-2 font-mono text-xs text-red-600 uppercase tracking-wider">
                        {cardError}
                      </p>
                    )}
                    {devMode && (
                      <p className="mt-2 font-mono text-xs text-amber-600">
                        Dev mode: Use 4242 4242 4242 4242 for success, 4000 0000 0000 0002 for decline
                      </p>
                    )}
                  </div>

                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleTopUp}
                    disabled={!topUpAmount || parseFloat(topUpAmount) < 1 || isProcessing || (!devMode && !stripe)}
                  >
                    {isProcessing ? 'PROCESSING...' : `ADD P$${topUpAmount || '0.00'}`}
                  </Button>

                  <p className="mt-4 font-mono text-xs text-gray-500 text-center">
                    P$ = Party Credits • 1 P$ = $1.00 USD
                  </p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="border-2 border-black bg-white p-4 text-center">
                <p className="font-mono text-2xl font-bold text-black">
                  P${wallet ? (wallet.totalEarned / 100).toFixed(0) : '0'}
                </p>
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  EARNED
                </p>
              </div>
              <div className="border-2 border-black bg-white p-4 text-center">
                <p className="font-mono text-2xl font-bold text-black">
                  P${wallet ? (wallet.totalSpent / 100).toFixed(0) : '0'}
                </p>
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  SPENT
                </p>
              </div>
              <div className="border-2 border-black bg-white p-4 text-center">
                <p className="font-mono text-2xl font-bold text-red-600">
                  P${wallet ? (wallet.totalFlakeFees / 100).toFixed(0) : '0'}
                </p>
                <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  FLAKE FEES
                </p>
              </div>
            </div>

            {/* Transaction History */}
            <div className="border-2 border-black bg-white">
              <div className="px-6 py-4 border-b-2 border-black">
                <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                  TRANSACTION HISTORY
                </h2>
              </div>

              {transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="font-mono text-sm text-gray-500 uppercase tracking-wider">
                    No transactions yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="px-6 py-4 flex items-center gap-4">
                      <div className="w-10 h-10 border-2 border-black flex items-center justify-center bg-gray-50">
                        <span className="font-mono text-lg">
                          {getTransactionIcon(tx.type)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                          {tx.typeDisplay || tx.type}
                        </p>
                        <p className="font-mono text-xs text-gray-500 truncate">
                          {tx.description || tx.eventTitle || formatDate(tx.createdAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-mono text-lg font-bold ${getTransactionColor(tx.amount)}`}>
                          {tx.amount >= 0 ? '+' : ''}P${(Math.abs(tx.amount) / 100).toFixed(2)}
                        </p>
                        <p className="font-mono text-xs text-gray-500">
                          Balance: P${(tx.balanceAfter / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
